'use client'

/**
 * Browser-tab favicon from the HQ app registry.
 *
 * Every internal app used to ship a static favicon file, so changing an
 * app's icon or colors in HQ → Apps never reached the tab. This hook builds
 * the favicon from the same registry entry the app bar renders: the admin's
 * uploaded tile image when there is one, else the Phosphor icon in `fgColor`
 * on a rounded `bgColor` square. Apps without a registry entry, or without
 * brand colors, keep their static favicon.
 *
 * `useAppFavicon` is mounted by `AppSidebar` (every switcher-headed internal
 * app) and `AppBar` (Leads); `SigninCard` drives `useTileFavicon` itself on
 * the signed-out sign-in page. Never mount either in a customer-facing app.
 */
import * as React from 'react'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'

import { APP_ICON_DEPTH } from '../app-icon-style'
import { PhosphorIcon } from '../primitives/icon-picker'
import { useOrdoApps } from './use-ordo-apps'

const TILE = 256
const ICON = 160

/**
 * The favicon SVG for a tile: rounded square in `bg`, `iconSvg` (a 256-unit
 * Phosphor `<svg>`) centered and scaled to 160 units. Pure; exported for tests.
 */
export function appFaviconSvg(iconSvg: string, bg: string): string {
  const offset = (TILE - ICON) / 2
  // Size the embedded icon by rewriting its own width/height; keep its viewBox.
  const icon = iconSvg
    .replace(/\swidth="[^"]*"/, '')
    .replace(/\sheight="[^"]*"/, '')
    .replace('<svg', `<svg x="${offset}" y="${offset}" width="${ICON}" height="${ICON}"`)
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${TILE} ${TILE}">` +
    `<rect width="${TILE}" height="${TILE}" rx="${TILE / 4}" fill="${bg}"/>` +
    `<defs><linearGradient id="app-depth" x1="${APP_ICON_DEPTH.x1}" y1="${APP_ICON_DEPTH.y1}" x2="${APP_ICON_DEPTH.x2}" y2="${APP_ICON_DEPTH.y2}">` +
    `<stop stop-color="white" stop-opacity="${APP_ICON_DEPTH.highlightOpacity}"/><stop offset="0.5" stop-opacity="0"/><stop offset="1" stop-color="black" stop-opacity="${APP_ICON_DEPTH.shadowOpacity}"/>` +
    `</linearGradient></defs><rect width="${TILE}" height="${TILE}" rx="${TILE / 4}" fill="url(#app-depth)"/>` +
    icon +
    `</svg>`
  )
}

export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/** Render a registry icon name to static SVG markup in `color`. */
function renderIconSvg(name: string, color: string): string | null {
  const host = document.createElement('div')
  const root = createRoot(host)
  flushSync(() => {
    root.render(<PhosphorIcon name={name} weight="bold" size={TILE} color={color} />)
  })
  const markup = host.innerHTML
  root.unmount()
  return markup.startsWith('<svg') ? markup : null
}

export function setFavicon(href: string, type: string): void {
  const head = document.head
  // Disable the app's static icons (keep apple-touch-icon: home-screen installs
  // use the bundled PNG) and point one managed <link> at the registry tile.
  // Retag them rather than remove(): Next renders `metadata.icons` as
  // React-owned nodes, and React throws unmounting a detached one, which
  // aborts every client navigation (PROD-1507).
  // ponytail: Next re-renders those links on navigation and this only runs when
  // the registry entry changes, so the static icon can win again after a route
  // change. Watch document.head if the tab icon must stay branded.
  for (const link of Array.from(head.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'))) {
    if (!link.dataset['registryFavicon']) {
      link.rel = 'disabled-icon'
    }
  }
  let link = head.querySelector<HTMLLinkElement>('link[data-registry-favicon]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    link.dataset['registryFavicon'] = 'true'
    head.appendChild(link)
  }
  if (link.href !== href) {
    link.type = type
    link.href = href
  }
}

/** A registry tile to draw as the favicon; `null` leaves the favicon alone. */
export interface FaviconTile {
  /** Admin-uploaded tile image URL — used as-is when present. */
  image?: string | null
  iconName: string | null
  bgColor: string | null
  fgColor: string | null
}

/**
 * Point the tab favicon at `tile`. Shared by the signed-in shell
 * (`useAppFavicon`, from `useOrdoApps`) and the signed-out sign-in card
 * (`SigninCard`, from `useAppBrand`), so both draw the identical tile.
 */
export function useTileFavicon(tile: FaviconTile | null | undefined): void {
  const image = tile?.image ?? null
  const iconName = tile?.iconName ?? null
  const bg = tile?.bgColor ?? null
  const fg = tile?.fgColor ?? null
  const hasTile = !!tile

  React.useEffect(() => {
    if (typeof document === 'undefined' || !hasTile) {
      return
    }
    if (image) {
      setFavicon(image, image.endsWith('.png') ? 'image/png' : 'image/svg+xml')
      return
    }
    if (!iconName || !bg || !fg) {
      return
    }
    // Rendering a React tree synchronously is not allowed inside an effect
    // commit; defer to a task.
    const timer = window.setTimeout(() => {
      const iconSvg = renderIconSvg(iconName, fg)
      if (iconSvg) {
        setFavicon(svgDataUrl(appFaviconSvg(iconSvg, bg)), 'image/svg+xml')
      }
    }, 0)
    return () => window.clearTimeout(timer)
  }, [hasTile, image, iconName, bg, fg])
}

export function useAppFavicon(currentAppId: string | undefined): void {
  const { apps } = useOrdoApps()
  useTileFavicon(currentAppId ? apps.find((a) => a.id === currentAppId) : undefined)
}
